import React, { useRef, useState } from "react";

/**
 * ContractSignaturePad — Type-to-sign or draw-to-sign for the
 * Purchase Plans Contract flow (owner + architect side).
 *
 * Parent passes `value` and `onChange({ method, value, typed_name })`.
 *   - typed:  value = the typed name itself
 *   - drawn:  value = canvas dataURL, typed_name = parent's full legal name
 *
 * No useEffect side-effects on state — every onChange is emitted from
 * a direct user event to stay clear of React 19's set-state-in-effect
 * lint and to avoid double-fire on mount.
 */
export default function ContractSignaturePad({
  value,
  onChange,
  testidPrefix = "contract-signature",
  placeholderName = "Type your full legal name",
}) {
  const [tab, setTab] = useState(value?.method === "drawn" ? "drawn" : "typed");
  const [typedName, setTypedName] = useState(
    value?.method === "typed"
      ? (value?.value || "")
      : (value?.typed_name || ""),
  );
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(
    value?.method === "drawn" && !!value?.value,
  );

  function switchTab(next) {
    setTab(next);
    if (next === "typed") {
      onChange?.({
        method: "typed",
        value: typedName.trim(),
        typed_name: typedName.trim(),
      });
    } else {
      const c = canvasRef.current;
      onChange?.({
        method: "drawn",
        value: hasDrawn && c ? c.toDataURL("image/png") : "",
        typed_name: typedName.trim(),
      });
    }
  }

  function handleTypedChange(e) {
    const v = e.target.value;
    setTypedName(v);
    if (tab === "typed") {
      onChange?.({
        method: "typed",
        value: v.trim(),
        typed_name: v.trim(),
      });
    } else {
      // drawn mode — only update the accompanying name
      const c = canvasRef.current;
      onChange?.({
        method: "drawn",
        value: hasDrawn && c ? c.toDataURL("image/png") : "",
        typed_name: v.trim(),
      });
    }
  }

  function clearCanvas() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
    onChange?.({ method: "drawn", value: "", typed_name: typedName.trim() });
  }

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return {
      x: ((t.clientX - rect.left) * c.width) / rect.width,
      y: ((t.clientY - rect.top) * c.height) / rect.height,
    };
  }

  function start(e) {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  }

  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#1B1F4B";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasDrawn) setHasDrawn(true);
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    const c = canvasRef.current;
    if (!c) return;
    onChange?.({
      method: "drawn",
      value: c.toDataURL("image/png"),
      typed_name: typedName.trim(),
    });
  }

  return (
    <div data-testid={`${testidPrefix}-wrap`}>
      <div role="tablist" className="flex" style={{ gap: 4, marginBottom: 12 }}>
        {[
          { id: "typed", label: "Type to sign" },
          { id: "drawn", label: "Draw signature" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`${testidPrefix}-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              style={{
                background: "transparent",
                border: "none",
                padding: "8px 0",
                marginRight: 20,
                cursor: "pointer",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: active ? 800 : 600,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: active ? "#1B1F4B" : "#6C6B85",
                position: "relative",
              }}
            >
              {t.label}
              {active && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 14,
                    bottom: -1,
                    height: 2,
                    background: "#B14E2F",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {tab === "typed" ? (
        <>
          <input
            type="text"
            placeholder={placeholderName}
            value={typedName}
            onChange={handleTypedChange}
            data-testid={`${testidPrefix}-typed-input`}
            style={{
              width: "100%",
              height: 44,
              padding: "0 14px",
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              background: "#FBFAFD",
              color: "#1B1F4B",
              fontSize: 15,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          />
          {typedName.trim() && (
            <div
              data-testid={`${testidPrefix}-typed-preview`}
              style={{
                marginTop: 10,
                padding: "14px 16px",
                background: "#FBFAFD",
                border: "1px dashed #D9D8E4",
                borderRadius: 4,
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 24,
                color: "#1B1F4B",
                lineHeight: 1.1,
              }}
            >
              {typedName.trim()}
              <div
                style={{
                  fontStyle: "normal",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontSize: 10.5,
                  color: "#6C6B85",
                  marginTop: 6,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                /s/ {typedName.trim()} · {new Date().toLocaleString("en-US")}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ position: "relative" }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={140}
              data-testid={`${testidPrefix}-canvas`}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              style={{
                width: "100%",
                height: 140,
                background: "#FBFAFD",
                border: "1px dashed #9AA0C0",
                borderRadius: 4,
                display: "block",
                cursor: "crosshair",
                touchAction: "none",
              }}
            />
            {!hasDrawn && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  color: "#9AA0C0",
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 14,
                }}
              >
                Sign here with your mouse or finger
              </span>
            )}
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
            <input
              type="text"
              placeholder="Type full legal name to accompany signature"
              value={typedName}
              onChange={handleTypedChange}
              data-testid={`${testidPrefix}-drawn-name`}
              style={{
                flex: 1,
                height: 36,
                padding: "0 12px",
                border: "1px solid #D9D8E4",
                borderRadius: 4,
                background: "#FBFAFD",
                color: "#1B1F4B",
                fontSize: 13,
              }}
            />
            <button
              type="button"
              onClick={clearCanvas}
              data-testid={`${testidPrefix}-clear`}
              style={{
                marginLeft: 10,
                height: 36,
                padding: "0 14px",
                background: "transparent",
                border: "1px solid #D9D8E4",
                borderRadius: 4,
                color: "#6C6B85",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "#6C6B85",
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
        }}
      >
        Your signature, name, and timestamp will be embedded in the executed PDF.
      </div>
    </div>
  );
}
